import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EMPTY, filter, firstValueFrom, interval, switchMap, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../../core/services/api.service';
import { AuthService } from '../../services/auth.service';
import { ApiEnvelope } from '../models/content.models';
import type { NotificationDto, NotificationListDto } from '../models/notifications.models';
import { AdminToastService } from '../admin/admin-toast.service';

const POLL_INTERVAL_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly toast = inject(AdminToastService);

  readonly notifications = signal<NotificationDto[]>([]);
  readonly unreadCount = signal(0);
  readonly loading = signal(false);
  readonly panelOpen = signal(false);

  private lifecycleBound = false;

  bindLifecycle(destroyRef: DestroyRef): void {
    if (this.lifecycleBound || typeof document === 'undefined') {
      return;
    }
    this.lifecycleBound = true;

    interval(POLL_INTERVAL_MS)
      .pipe(
        filter(() => this.auth.isAuthenticated() && document.visibilityState === 'visible'),
        switchMap(() => this.fetchUnreadCount()),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe();

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && this.auth.isAuthenticated()) {
        void this.loadUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      this.lifecycleBound = false;
    });

    void this.loadUnreadCount();
  }

  openPanel(): void {
    this.panelOpen.set(true);
    void this.load();
  }

  closePanel(): void {
    this.panelOpen.set(false);
  }

  togglePanel(): void {
    if (this.panelOpen()) {
      this.closePanel();
      return;
    }
    this.openPanel();
  }

  load(): Promise<void> {
    this.loading.set(true);
    return this.fetchList()
      .then((data) => {
        this.notifications.set(data.items);
        this.unreadCount.set(data.unreadCount);
      })
      .catch(() => {
        this.toast.error('Could not load notifications');
      })
      .finally(() => {
        this.loading.set(false);
      });
  }

  loadUnreadCount(): Promise<void> {
    if (!this.auth.isAuthenticated()) {
      return Promise.resolve();
    }

    return firstValueFrom(this.fetchUnreadCount())
      .then(() => undefined)
      .catch(() => undefined);
  }

  markRead(id: string): Promise<void> {
    return firstValueFrom(
      this.api.patch<ApiEnvelope<NotificationDto>>(`${this.areaPrefix()}/notifications/${id}/read`, {}),
    )
      .then((response) => {
        const updated = response.data;
        this.notifications.update((items) =>
          items.map((item) => (item._id === id ? { ...item, readAt: updated.readAt } : item)),
        );
        this.unreadCount.update((count) => Math.max(0, count - 1));
      })
      .catch(() => {
        this.toast.error('Could not mark notification as read');
      });
  }

  markAllRead(): Promise<void> {
    return firstValueFrom(
      this.api.patch<ApiEnvelope<{ updated: number }>>(`${this.areaPrefix()}/notifications/read-all`, {}),
    )
      .then(() => {
        const now = new Date().toISOString();
        this.notifications.update((items) => items.map((item) => ({ ...item, readAt: item.readAt ?? now })));
        this.unreadCount.set(0);
      })
      .catch(() => {
        this.toast.error('Could not mark all notifications as read');
      });
  }

  clearAll(): Promise<void> {
    return firstValueFrom(
      this.api.delete<ApiEnvelope<{ deleted: number }>>(`${this.areaPrefix()}/notifications`),
    )
      .then(() => {
        this.notifications.set([]);
        this.unreadCount.set(0);
      })
      .catch(() => {
        this.toast.error('Could not clear notifications');
      });
  }

  async navigateTo(notification: NotificationDto): Promise<void> {
    if (!notification.readAt) {
      await this.markRead(notification._id);
    }

    const area = this.auth.isPlatformSession() ? 'platform' : 'tenant';
    const commands: string[] = [];
    const queryParams: Record<string, string> = {};

    if (notification.entityType === 'lead') {
      commands.push(`/${area}/leads/${notification.entityId}`);
    } else if (notification.entityType === 'applicant') {
      commands.push(`/${area}/applicants/${notification.entityId}`);
    } else if (notification.entityType === 'task') {
      const parentType =
        (notification.metadata?.['parentEntityType'] as string | undefined) ?? 'lead';
      const resource = parentType === 'applicant' ? 'applicants' : 'leads';
      commands.push(`/${area}/${resource}/${notification.entityId}`);
      if (notification.taskId) {
        queryParams['taskId'] = notification.taskId;
      }
    } else if (notification.entityType === 'feature_request') {
      commands.push(`/platform/feature-requests/${notification.entityId}`);
    }

    this.closePanel();
    if (commands.length > 0) {
      await this.router.navigate(commands, { queryParams });
    }
  }

  private areaPrefix(): string {
    return this.auth.isPlatformSession() ? 'platform' : 'tenant';
  }

  private fetchList(): Promise<NotificationListDto> {
    return firstValueFrom(
      this.api.get<ApiEnvelope<NotificationListDto>>(`${this.areaPrefix()}/notifications`),
    ).then((response) => response.data);
  }

  private fetchUnreadCount() {
    if (!this.auth.isAuthenticated()) {
      return EMPTY;
    }

    return this.api
      .get<ApiEnvelope<{ count: number }>>(`${this.areaPrefix()}/notifications/unread-count`)
      .pipe(
        map((r) => r.data.count),
        tap((count) => this.unreadCount.set(count)),
      );
  }
}
